/**
 * me/index.tsx — Student dashboard.
 *
 * Replaces the Phase B placeholder. The four widgets from N-05 surface
 * their real (typed) shapes backed by /api/progress/me.
 *
 * Routes:
 *   /me          — landing with progress + activity feed (this file)
 *   /me/progress — full-fidelity view with badge grid (later in this phase)
 *   /me/badges   — standalone badge grid (Fase 6 N-06 polish)
 */

import type { ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, redirect, Link } from '@tanstack/react-router';
import { ProgressHeader } from '../../components/student/ProgressHeader.js';
import { GameProgressCard } from '../../components/student/GameProgressCard.js';
import { WorksheetProgressCard } from '../../components/student/WorksheetProgressCard.js';
import { RecentActivityFeed } from '../../components/student/RecentActivityFeed.js';
import {
  freshSessionQuery,
  myProgressQuery,
  myBadgesQuery,
  type SessionPrincipal,
  type ProgressSummary,
} from '../../lib/api/queries.js';

export const Route = createFileRoute('/me/')({
  beforeLoad: async ({ context, location }) => {
    // Force a fresh session fetch — same pattern as /me/progress. Without
    // this, /me relies on the root loader's cached data, which can be
    // empty on the first navigation (e.g., E2E tests).
    const session = await context.queryClient.fetchQuery(freshSessionQuery);
    const user = session?.ok ? (session.data?.user ?? null) : null;
    if (!user) {
      throw redirect({
        to: '/login',
        search: { error: undefined, redirect: location.href },
      });
    }
    if (user.role === 'admin') {
      throw redirect({ to: '/admin' });
    }
    if (user.role === 'tutor' && user.isApprovedTutor) {
      throw redirect({ to: '/dashboard' });
    }
    if (user.role === 'parent') {
      // N-17: parent dashboard ships at /parent. Don't let parents see the
      // student dashboard — redirect them to their own home.
      throw redirect({ to: '/parent' });
    }
    return null;
  },
  loader: async ({ context }) => {
    await context.queryClient.prefetchQuery(myProgressQuery);
    return null;
  },
  component: StudentDashboard,
});

function StudentDashboard(): ReactNode {
  const { queryClient } = Route.useRouteContext();

  const session = queryClient.getQueryData(freshSessionQuery.queryKey) as
    { ok?: boolean; data?: { user?: { name: string } } } | undefined;
  const studentName = session?.ok ? (session.data?.user?.name ?? 'Siswa') : 'Siswa';

  // useQuery drives the live subscription; the loader's prefetch warms the
  // cache so the first paint shows data immediately.
  const liveQuery = useQuery(myProgressQuery);
  const badgesQuery = useQuery(myBadgesQuery());
  const recentBadges = badgesQuery.data?.ok
    ? badgesQuery.data.data.earned
        .slice()
        .sort((a, b) => (b.awardedAt ?? '').localeCompare(a.awardedAt ?? ''))
        .slice(0, 3)
    : [];
  const progress: ProgressSummary | null = liveQuery.data?.ok ? liveQuery.data.data : null;

  const aggregate = progress?.aggregate ?? {
    gamesStarted: 0,
    gamesCompleted: 0,
    totalStars: 0,
    worksheetsCompleted: 0,
    totalWorksheets: 0,
    streakDays: 0,
    badgesEarned: 0,
  };
  const games = progress?.games ?? [];
  const worksheets = progress?.worksheets ?? [];
  const recent = progress?.recentActivity ?? [];

  return (
    <main className="page-shell" data-testid="me-dashboard">
      <ProgressHeader studentName={studentName} aggregate={aggregate} recentBadges={recentBadges} />

      <section className="section" aria-labelledby="next-action">
        <h2 className="section-title" id="next-action">
          Langkah berikutnya
        </h2>
        <div className="cluster">
          <Link className="button button-secondary" to="/me/progress">
            Lihat progres lengkap
          </Link>
          <Link className="button button-ghost" to="/games">
            Cari game publik
          </Link>
        </div>
      </section>

      <section className="section" aria-labelledby="games-section">
        <h2 className="section-title" id="games-section">
          Progres game
        </h2>
        {games.length > 0 ? (
          <div className="card-grid">
            {games.map((entry) => (
              <GameProgressCard key={entry.gameId} entry={entry} />
            ))}
          </div>
        ) : (
          <p className="form-message" style={{ color: 'var(--color-ink-300)' }}>
            Belum ada game yang dimulai. Coba <Link to="/games">katalog game publik</Link> untuk
            mulai.
          </p>
        )}
      </section>

      <section className="section" aria-labelledby="worksheets-section">
        <h2 className="section-title" id="worksheets-section">
          Worksheet
        </h2>
        {worksheets.length > 0 ? (
          <div className="card-grid">
            {worksheets.map((entry) => (
              <WorksheetProgressCard key={entry.worksheetId} entry={entry} />
            ))}
          </div>
        ) : (
          <p className="form-message" style={{ color: 'var(--color-ink-300)' }}>
            Worksheet belum tersedia — kelas kamu belum membuka worksheet apa pun.
          </p>
        )}
      </section>

      <section className="section" aria-labelledby="activity-section">
        <h2 className="section-title" id="activity-section">
          Aktivitas terbaru
        </h2>
        <RecentActivityFeed events={recent} limit={6} />
      </section>
    </main>
  );
}
